import { Chip } from "@heroui/react";
import { Sheet } from "@heroui-pro/react";
import { Package } from "lucide-react";
import { useRef } from "react";

import { PersonAvatar, fullName } from "@/components/sales/shared";
import { UserCart } from "@/types/cart";
import { formatCurrency } from "@/utils/currency";

interface CartViewModalProps {
  isOpen: boolean;
  cart: UserCart | null;
  onClose: () => void;
}

function ProductImage({ src, alt }: { src?: string; alt: string }) {
  return (
    <span className="bg-(--default) flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl">
      {src ? (
        <img alt={alt} className="size-full object-contain" src={src} />
      ) : (
        <Package className="text-muted size-4" />
      )}
    </span>
  );
}

const th = "text-muted px-2 py-2 text-xs font-medium";

export const CartViewModal = ({
  cart,
  isOpen,
  onClose,
}: CartViewModalProps) => {
  // Keep the last cart while the sheet slides out.
  const last = useRef<UserCart | null>(null);

  if (cart) last.current = cart;
  const shown = cart ?? last.current;

  return (
    <Sheet
      isDetached
      isOpen={isOpen && !!shown}
      placement="right"
      onOpenChange={(open) => !open && onClose()}
    >
      <Sheet.Backdrop>
        <Sheet.Content className="h-[calc(100%-1rem)] w-full max-w-2xl">
          <Sheet.Dialog>
            {shown ? <CartBody cart={shown} /> : null}
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
};

function CartBody({ cart }: { cart: UserCart }) {
  const name = fullName(cart.user);

  return (
    <>
      <Sheet.CloseTrigger />
      <Sheet.Header>
        <Sheet.Heading>Cart Details</Sheet.Heading>
        <p className="text-muted font-mono text-xs">ID: {cart.id}</p>
      </Sheet.Header>
      <Sheet.Body className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto">
        <section className="flex flex-col gap-3">
          <h3 className="text-muted text-xs font-medium">Customer</h3>
          <div className="flex items-center gap-3">
            <PersonAvatar name={name} size="md" />
            <div className="flex min-w-0 flex-1 flex-col leading-snug">
              <span className="font-medium">{name || "No name"}</span>
              <a
                className="text-muted decoration-separator text-sm underline underline-offset-4"
                href={`tel:${cart.user.phoneNumber}`}
              >
                {cart.user.phoneNumber}
              </a>
            </div>
            <Chip
              color={!cart.user.firstOrderId ? "success" : "default"}
              variant="soft"
            >
              {!cart.user.firstOrderId ? "First Order" : "Returning Customer"}
            </Chip>
          </div>
        </section>

        {cart.items.length > 0 ? (
          <section className="flex flex-col gap-1">
            <h3 className="text-muted text-xs font-medium">
              Products ({cart.items.length})
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-separator border-b text-start">
                  <th className={`${th} text-start`}>Item</th>
                  <th className={`${th} text-start`}>Supplier</th>
                  <th className={`${th} text-end`}>Qty</th>
                  <th className={`${th} text-end`}>Price</th>
                  <th className={`${th} text-end`}>Total</th>
                </tr>
              </thead>
              <tbody className="divide-separator divide-y">
                {cart.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-3">
                        <ProductImage
                          alt={item.product.name}
                          src={item.product.images?.[0]}
                        />
                        <div className="flex min-w-0 flex-col leading-snug">
                          <span className="font-medium">
                            {item.product.name}
                          </span>
                          {item.product.nameAr ? (
                            <span
                              className="text-muted text-xs"
                              dir="rtl"
                              lang="ar"
                            >
                              {item.product.nameAr}
                            </span>
                          ) : null}
                          {item.promotion ? (
                            <Chip
                              className="mt-1 self-start"
                              color="success"
                              size="sm"
                              variant="soft"
                            >
                              {item.promotion.name}
                            </Chip>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="text-muted px-2">
                      {item.product.supplier?.name ?? "—"}
                    </td>
                    <td className="px-2 text-end tabular-nums">
                      {item.quantity}
                    </td>
                    <td className="px-2 text-end tabular-nums">
                      {item.compareAtPrice !== null ? (
                        <span className="text-muted block text-xs line-through">
                          {formatCurrency(item.compareAtPrice)}
                        </span>
                      ) : null}
                      {formatCurrency(item.price)}
                    </td>
                    <td className="px-2 text-end font-medium tabular-nums">
                      {formatCurrency(item.lineTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        {cart.specialItems.length > 0 ? (
          <section className="flex flex-col gap-1">
            <h3 className="text-muted text-xs font-medium">
              Special Items ({cart.specialItems.length})
            </h3>
            <ul className="divide-separator divide-y">
              {cart.specialItems.map((specialItem) => (
                <li
                  key={specialItem.id}
                  className="flex items-center gap-3 px-2 py-2.5"
                >
                  <ProductImage alt={specialItem.name} />
                  <div className="flex min-w-0 flex-1 flex-col leading-snug">
                    <span className="font-medium">{specialItem.name}</span>
                    {specialItem.description ? (
                      <span className="text-muted text-xs">
                        {specialItem.description}
                      </span>
                    ) : null}
                  </div>
                  <span className="text-sm tabular-nums">
                    × {specialItem.quantity}
                  </span>
                  <span className="w-40 text-end text-sm tabular-nums">
                    {formatCurrency(specialItem.minPrice)} –{" "}
                    {formatCurrency(specialItem.maxPrice)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="flex flex-col gap-2">
          <h3 className="text-muted text-xs font-medium">Summary</h3>
          <dl className="flex flex-col gap-2 text-sm tabular-nums">
            <Row label="Subtotal" value={formatCurrency(cart.subTotal)} />
            {cart.promotionDiscount > 0 ? (
              <Row
                label="Promotions (in subtotal)"
                tone="text-(--success)"
                value={`-${formatCurrency(cart.promotionDiscount)}`}
              />
            ) : null}
            {cart.discount > 0 ? (
              <Row
                label="Discount"
                tone="text-(--success)"
                value={`-${formatCurrency(cart.discount)}`}
              />
            ) : null}
            {cart.appliedCoupons.map((coupon) => (
              <Row
                key={coupon.code}
                label={`Code ${coupon.code} saved`}
                value={formatCurrency(coupon.amount)}
              />
            ))}
            <Row label="VAT" value={formatCurrency(cart.vat)} />
            <Row label="Delivery fee" value={formatCurrency(cart.deliveryFee)} />
            {cart.remainingToMinimum > 0 ? (
              <Row
                label="Remaining to minimum order"
                tone="text-(--warning)"
                value={formatCurrency(cart.remainingToMinimum)}
              />
            ) : null}
          </dl>
          <div className="border-separator mt-2 flex items-baseline justify-between border-t pt-3">
            <span className="font-semibold">Total</span>
            <span className="text-xl font-semibold tabular-nums">
              {cart.total ? formatCurrency(cart.total) : "N/A"}
            </span>
          </div>
          {cart.minimumOrder > 0 ? (
            <p className="text-muted text-xs">
              Minimum order: {formatCurrency(cart.minimumOrder)}
            </p>
          ) : null}
        </section>
      </Sheet.Body>
    </>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className={`flex justify-between ${tone ?? ""}`}>
      <dt className={tone ? "" : "text-muted"}>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
